import { deviceLocal } from '@/privacy/deviceStorage';
import { useCallback, useEffect, useState } from 'react';
import {
  PHONE_BADGE_COUNTS,
  PHONE_SWITCHER_ORDER,
  type IPhonePreferences,
  type PhonePreferencesSaveType,
  type PhoneBadgeCountsType,
  type PhoneSwitcherOrderType,
} from '@featit/shared/types/index';
import { useStores } from '@/hooks/useStores';
import { userPreferencesApi } from '@/services/UserPreferencesApi';
import {
  failureToSay,
  type HeldSentenceType,
  sayFailure,
  sentenceToSay,
} from '@/services/actionFailure';
import { useT } from '@/i18n/LocaleProvider';

/**
 * IU-4 (`docs/concept-iphone-ux.md#iu-4-phone-type-and-spacing`, *Settings*): every phone setting,
 * read-through then saved by whichever one a screen edits — Density today, every other one ahead
 * of its own UI. The same read/refresh/save shape as `useToolbarPreferences`, without that hook's
 * cross-instance publish/cache: only one screen edits these so far, so there is nothing yet for a
 * second instance to race.
 */
/**
 * F-0530 (AC-19.8): every mounted copy in this web view hears a save, so the badge (`ShellRuntime`'s
 * copy) counts by the setting the moment Settings saves it, not on its next read.
 */
const savedListeners = new Set<(saved: IPhonePreferences) => void>();

/** F-0530 (AC-19.8): "Badge counts" as every web view shares it, for counting the badge. */
const BADGE_COUNTS_KEY = 'featit:badgeCounts';

/**
 * F-0532 (AC-19.18): "Work area switcher order" as every web view shares it: the sheet opens from
 * any tab's scope line or the drawer, whichever web view last read or saved the setting.
 */
const SWITCHER_ORDER_KEY = 'featit:switcherOrder';

function shareBadgeCounts(preferences: IPhonePreferences): void {
  try {
    if (preferences.badgeCounts) deviceLocal.setItem(BADGE_COUNTS_KEY, preferences.badgeCounts);
    if (preferences.switcherOrder) {
      deviceLocal.setItem(SWITCHER_ORDER_KEY, preferences.switcherOrder);
    }
  } catch {
    // Storage refused: each web view counts by its own copy, as before.
  }
}

/**
 * The switcher order as last read or saved in any of the app's web views. Recent first (AC-19.3)
 * before that, from a server older than the setting, or when storage is refused.
 */
export function sharedSwitcherOrder(): PhoneSwitcherOrderType {
  try {
    const stored = deviceLocal.getItem(SWITCHER_ORDER_KEY);
    const known: readonly string[] = Object.values(PHONE_SWITCHER_ORDER);
    return stored && known.includes(stored)
      ? (stored as PhoneSwitcherOrderType)
      : PHONE_SWITCHER_ORDER.RECENT_THEN_CUSTOM;
  } catch {
    return PHONE_SWITCHER_ORDER.RECENT_THEN_CUSTOM;
  }
}

/** The setting as last read or saved in any of the app's web views, or `null` before that. */
export function sharedBadgeCounts(): PhoneBadgeCountsType | null {
  try {
    const stored = deviceLocal.getItem(BADGE_COUNTS_KEY);
    return stored === PHONE_BADGE_COUNTS.WORK_AREA || stored === PHONE_BADGE_COUNTS.ALL_WORK_AREAS
      ? stored
      : null;
  } catch {
    return null;
  }
}

export function usePhonePreferences() {
  const t = useT();
  const { authStore } = useStores();
  const userId = authStore.user?.id ?? null;
  const [preferences, setPreferences] = useState<IPhonePreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<HeldSentenceType | null>(null);

  const refresh = useCallback(async (): Promise<IPhonePreferences | null> => {
    if (!userId) return null;
    try {
      const next = await userPreferencesApi.phone();
      setPreferences(next);
      shareBadgeCounts(next);
      setError(null);
      return next;
    } catch (cause) {
      setError(failureToSay(cause, 'settingsAccount.phone.actions.load'));
      return null;
    }
  }, [userId]);

  useEffect(() => {
    setPreferences(null);
    setError(null);
    if (!userId) return;
    void refresh();
  }, [refresh, userId]);

  useEffect(() => {
    savedListeners.add(setPreferences);
    return () => {
      savedListeners.delete(setPreferences);
    };
  }, []);

  const save = useCallback(
    async (next: PhonePreferencesSaveType) => {
      if (!userId) throw new Error('Sign in to save your phone settings.');
      setSaving(true);
      setError(null);
      try {
        const saved = await userPreferencesApi.updatePhone(next);
        setPreferences(saved);
        shareBadgeCounts(saved);
        savedListeners.forEach((listener) => listener(saved));
        return saved;
      } catch (cause) {
        // Held, and worded where it is drawn (the settings screen, which registers `settingsAccount`).
        setError(
          httpStatus(cause) === 409
            ? sentenceToSay('settingsAccount.phone.changedElsewhere')
            : failureToSay(cause, 'settingsAccount.phone.actions.save'),
        );
        throw cause;
      } finally {
        setSaving(false);
      }
    },
    [userId],
  );

  return { preferences, saving, error: sayFailure(t, error), refresh, save };
}

/** Every phone preference that asks before an act (each `confirmBefore…`), read by {@link confirmsBefore}. */
export type PhoneConfirmBeforeType = Extract<keyof IPhonePreferences, `confirmBefore${string}`>;

/**
 * I-1201: whether to ask before the act `which` guards. ⛔ Unread is never "don't ask": while the
 * preferences have not arrived (or could not be read), it asks. Withdraw tapped before the one GET
 * answered once withdrew a decision without the confirmation its reader had turned on, because the
 * unread preference read as its default, off. Asking once too often costs a tap; not asking cannot
 * be taken back.
 */
export function confirmsBefore(
  preferences: Pick<IPhonePreferences, PhoneConfirmBeforeType> | null,
  which: PhoneConfirmBeforeType,
): boolean {
  return preferences === null ? true : Boolean(preferences[which]);
}

function httpStatus(cause: unknown): number | undefined {
  return (cause as { response?: { status?: number } })?.response?.status;
}
